import React, { useEffect, useState } from 'react';
import { Card, Button, Empty, Spin, Alert, message, Badge, Tag, Row, Col, Typography, Space, Modal } from 'antd';
import { GiftOutlined, PlusOutlined, TrophyOutlined, FireOutlined, ArrowLeftOutlined, QuestionCircleOutlined, PlayCircleOutlined, DollarCircleOutlined } from '@ant-design/icons';
import referralService from '../../../../services/referral/referral.service';
import { ReferralCampaign } from '../../../../types/referral.types';
import ShareCode from './ShareCode';
import { formatRewardValue, getRewardTypeLabel } from '../../../../utils/referralUtils';

const { Title, Text, Paragraph } = Typography;

const CreateReferralCode: React.FC = () => {
  const [campaigns, setCampaigns] = useState<ReferralCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState<{ [campaignId: string]: boolean }>({});
  const [campaignCodes, setCampaignCodes] = useState<{ [campaignId: string]: string }>({}); // Her kampanya için kod bilgisi
  const [selectedCampaign, setSelectedCampaign] = useState<ReferralCampaign | null>(null);
  const [selectedCode, setSelectedCode] = useState<string | null>(null); // Gösterilecek kod
  const [error, setError] = useState<string | null>(null);
  const [infoModalVisible, setInfoModalVisible] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await referralService.getCampaigns();
      setCampaigns(data);
    } catch (err: any) {
      setError('Kampanyalar yüklenirken bir hata oluştu.');
      console.error('Error fetching campaigns:', err);
    } finally {
      setLoading(false);
    }
  };

  // v2.0: Mevcut kodları kontrol et - hangi kampanyalar için kod var?
  const fetchExistingCodes = async (campaignsList: ReferralCampaign[]) => {
    try {
      const history = await referralService.getHistory();
      // Referans geçmişinden kampanya isimlerine göre kodları eşleştir
      const codesMap: { [campaignName: string]: string } = {};
      history.forEach((item: any) => {
        if (item.code && item.campaignName) {
          codesMap[item.campaignName] = item.code;
        }
      });
      
      // Kampanya ID'leri ile eşleştir
      const campaignCodesMap: { [campaignId: string]: string } = {};
      campaignsList.forEach((campaign) => {
        if (codesMap[campaign.name]) {
          campaignCodesMap[campaign.id] = codesMap[campaign.name];
        }
      });
      setCampaignCodes(campaignCodesMap);
    } catch (err: any) {
      console.error('Error fetching existing codes:', err);
      // Hata durumunda sessizce devam et
    }
  };

  // Kampanyalar yüklendiğinde mevcut kodları eşleştir
  useEffect(() => {
    if (campaigns.length > 0) {
      fetchExistingCodes(campaigns);
    }
  }, [campaigns]);

  // v2.0: "get or create" mantığı - kod varsa getir, yoksa oluştur
  const handleCreateCode = async (campaignId: string) => {
    try {
      setCreating({ ...creating, [campaignId]: true });
      setError(null);
      
      // API çağrısı - v2.0'da bu endpoint "get or create" mantığıyla çalışıyor
      // Eğer kod varsa mevcut kodu döner, yoksa yeni kod oluşturur
      const response = await referralService.createCode({ campaignId });
      
      // Kampanya için kod bilgisini state'e kaydet
      setCampaignCodes({ ...campaignCodes, [campaignId]: response.code });
      
      // Gösterilecek kampanya ve kodu set et
      const campaign = campaigns.find(c => c.id === campaignId);
      setSelectedCampaign(campaign || null);
      setSelectedCode(response.code);
      
      // Kod ilk kez oluşturulduysa veya mevcut kod gösterildiyse mesaj göster
      const wasExisting = campaignCodes[campaignId];
      if (wasExisting) {
        message.success('Davet kodunuz gösteriliyor!');
      } else {
        message.success('Referans kodu başarıyla oluşturuldu!');
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || 'Kod oluşturulurken bir hata oluştu.';
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setCreating({ ...creating, [campaignId]: false });
    }
  };

  const handleReset = () => {
    setSelectedCode(null);
    setSelectedCampaign(null);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error && campaigns.length === 0) {
    return (
      <Alert
        message="Hata"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={fetchCampaigns}>
            Tekrar Dene
          </Button>
        }
      />
    );
  }

  if (selectedCode) {
    return (
      <div>
        {/* Başarı Kartı - Mor Gradient */}
        <Card
          style={{
            textAlign: 'center',
            background: 'linear-gradient(135deg, #7338a1 0%, #9d4edd 50%, #c77dff 100%)',
            color: 'white',
            marginBottom: '24px',
            border: 'none',
            borderRadius: '16px',
            boxShadow: '0 8px 24px rgba(115, 56, 161, 0.3)',
            overflow: 'hidden',
            position: 'relative'
          }}
          bodyStyle={{ padding: '40px 24px' }}
        >
          {/* Dekoratif arka plan elemanları */}
          <div style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '200px',
            height: '200px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            zIndex: 0
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-30px',
            left: '-30px',
            width: '150px',
            height: '150px',
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '50%',
            zIndex: 0
          }} />
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              display: 'inline-block',
              padding: '20px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              marginBottom: '20px',
              backdropFilter: 'blur(10px)'
            }}>
              <GiftOutlined style={{ fontSize: '64px', color: 'white' }} />
            </div>
            <Title level={2} style={{ color: 'white', marginBottom: '8px', fontWeight: 'bold' }}>
              Kodunuz Hazır!
            </Title>
            <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px', display: 'block', marginBottom: '24px' }}>
              Referans kodunuzu paylaşarak ödüller kazanmaya başlayın
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <div
                style={{
                  fontSize: '36px',
                  fontWeight: 'bold',
                  letterSpacing: '6px',
                  padding: '24px',
                  background: 'rgba(255, 255, 255, 0.25)',
                  borderRadius: '12px',
                  fontFamily: 'monospace',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  backdropFilter: 'blur(10px)',
                  display: 'inline-block',
                  color: 'white'
                }}
              >
                {selectedCode}
              </div>
              <ShareCode code={selectedCode} />
            </div>
          </div>
        </Card>

        {/* Kampanya Detayları Kartı */}
        {selectedCampaign && (
          <Card 
            title={
              <Space>
                <FireOutlined style={{ color: '#7338a1' }} />
                <span>Kampanya Detayları</span>
              </Space>
            }
            style={{ 
              marginBottom: '24px',
              borderRadius: '12px',
              border: '1px solid #e8e8e8',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong style={{ color: '#595959' }}>Kampanya:</Text>
                  <div style={{ marginTop: '4px', fontSize: '16px', color: '#7338a1', fontWeight: 600 }}>
                    {selectedCampaign.name}
                  </div>
                </div>
              </Col>
              {selectedCampaign.description && (
                <Col xs={24}>
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ color: '#595959' }}>Açıklama:</Text>
                    <div style={{ marginTop: '4px', color: '#8c8c8c' }}>
                      {selectedCampaign.description}
                    </div>
                  </div>
                </Col>
              )}
              <Col xs={24} sm={12}>
                <Card
                  style={{
                    background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
                    border: '1px solid #c77dff',
                    borderRadius: '8px'
                  }}
                >
                  <Text strong style={{ color: '#7338a1', display: 'block', marginBottom: '8px' }}>
                    Yeni Şirket Ödülü
                  </Text>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#7338a1' }}>
                    {formatRewardValue(selectedCampaign.newCompanyRewardType, selectedCampaign.newCompanyRewardValue)}
                  </div>
                  <Tag color="purple" style={{ marginTop: '8px' }}>
                    {getRewardTypeLabel(selectedCampaign.newCompanyRewardType)}
                  </Tag>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card
                  style={{
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    border: '1px solid #fbbf24',
                    borderRadius: '8px'
                  }}
                >
                  <Text strong style={{ color: '#92400e', display: 'block', marginBottom: '8px' }}>
                    Sizin Ödülünüz
                  </Text>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#92400e' }}>
                    {formatRewardValue(selectedCampaign.referringCompanyRewardType, selectedCampaign.referringCompanyRewardValue)}
                  </div>
                  <Tag color="gold" style={{ marginTop: '8px' }}>
                    {getRewardTypeLabel(selectedCampaign.referringCompanyRewardType)}
                  </Tag>
                </Card>
              </Col>
            </Row>
          </Card>
        )}

        <Button 
          onClick={handleReset} 
          icon={<ArrowLeftOutlined />}
          block 
          size="large"
          style={{
            height: '48px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 600,
            background: 'white',
            border: '2px solid #7338a1',
            color: '#7338a1',
            boxShadow: '0 2px 8px rgba(115, 56, 161, 0.15)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f5f0ff';
            e.currentTarget.style.borderColor = '#9d4edd';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
            e.currentTarget.style.borderColor = '#7338a1';
          }}
        >
          Geri
        </Button>
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <Empty
        description="Şu anda aktif kampanya bulunmamaktadır"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #7338a1 0%, #9d4edd 50%, #c77dff 100%)',
        borderRadius: '16px',
        padding: '40px 24px',
        marginBottom: '32px',
        textAlign: 'center',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(115, 56, 161, 0.3)'
      }}>
        {/* Sağ üst köşede bilgi butonu */}
        <Button
          type="default"
          icon={<QuestionCircleOutlined />}
          onClick={() => setInfoModalVisible(true)}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: 'white',
            borderRadius: '8px',
            backdropFilter: 'blur(10px)',
            zIndex: 2
          }}
        >
          Referans Programı Nedir?
        </Button>

        {/* Dekoratif arka plan */}
        <div style={{
          position: 'absolute',
          top: '-100px',
          right: '-100px',
          width: '300px',
          height: '300px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          zIndex: 0
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-80px',
          left: '-80px',
          width: '250px',
          height: '250px',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '50%',
          zIndex: 0
        }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-block',
            padding: '16px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            marginBottom: '16px',
            backdropFilter: 'blur(10px)'
          }}>
            <GiftOutlined style={{ fontSize: '48px', color: 'white' }} />
          </div>
          <Title level={2} style={{ color: 'white', marginBottom: '12px', fontWeight: 'bold' }}>
            Referans Programına Katılın
          </Title>
          <Paragraph style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px', marginBottom: 0, maxWidth: '600px', margin: '0 auto' }}>
            Bir kampanya seçerek kişisel referans kodunuzu oluşturun ve arkadaşlarınızı davet ederek ödüller kazanın!
          </Paragraph>
        </div>
      </div>

      {error && (
        <Alert
          message="Hata"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: '24px', borderRadius: '8px' }}
        />
      )}

      {/* Kampanya Kartları */}
      <Row gutter={[24, 24]}>
        {campaigns.map((campaign, index) => (
          <Col xs={24} sm={12} lg={8} key={campaign.id}>
            <Card
              hoverable
              style={{
                borderRadius: '16px',
                border: '2px solid #e8e8e8',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                position: 'relative',
                overflow: 'hidden'
              }}
              bodyStyle={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column' }}
            >
              {/* Üst köşe badge */}
              <div style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                zIndex: 2
              }}>
                <Badge.Ribbon 
                  text={index === 0 ? "Popüler" : "Yeni"} 
                  color={index === 0 ? "purple" : "magenta"}
                />
              </div>

              {/* Kampanya Başlığı */}
              <div style={{ marginBottom: '16px', paddingRight: '80px' }}>
                <Title level={4} style={{ 
                  marginBottom: '8px',
                  color: '#7338a1',
                  fontWeight: 'bold',
                  fontSize: '20px'
                }}>
                  {campaign.name}
                </Title>
                {campaign.description && (
                  <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>
                    {campaign.description}
                  </Text>
                )}
              </div>

              {/* Ödül Bilgileri */}
              <div style={{ flex: 1, marginBottom: '20px' }}>
                {/* Yeni Şirket Ödülü */}
                <Card
                  size="small"
                  style={{
                    background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
                    border: '1px solid #c77dff',
                    borderRadius: '8px',
                    marginBottom: '12px'
                  }}
                  bodyStyle={{ padding: '12px' }}
                >
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text strong style={{ color: '#7338a1', fontSize: '12px' }}>
                      Yeni Şirket Ödülü
                    </Text>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#7338a1' }}>
                      {formatRewardValue(campaign.newCompanyRewardType, campaign.newCompanyRewardValue)}
                    </div>
                    <Tag color="purple" style={{ margin: 0 }}>
                      {getRewardTypeLabel(campaign.newCompanyRewardType)}
                    </Tag>
                  </Space>
                </Card>

                {/* Sizin Ödülünüz */}
                <Card
                  size="small"
                  style={{
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    border: '1px solid #fbbf24',
                    borderRadius: '8px'
                  }}
                  bodyStyle={{ padding: '12px' }}
                >
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text strong style={{ color: '#92400e', fontSize: '12px' }}>
                      <TrophyOutlined /> Sizin Ödülünüz
                    </Text>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#92400e' }}>
                      {formatRewardValue(campaign.referringCompanyRewardType, campaign.referringCompanyRewardValue)}
                    </div>
                    <Tag color="gold" style={{ margin: 0 }}>
                      {getRewardTypeLabel(campaign.referringCompanyRewardType)}
                    </Tag>
                  </Space>
                </Card>
              </div>

              {/* Kod Oluştur / Davet Kodumu Göster Butonu */}
              <Button
                type="primary"
                icon={campaignCodes[campaign.id] ? <GiftOutlined /> : <PlusOutlined />}
                onClick={() => handleCreateCode(campaign.id)}
                loading={creating[campaign.id]}
                disabled={creating[campaign.id]}
                block
                size="large"
                style={{
                  height: '48px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  background: campaignCodes[campaign.id] 
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                    : 'linear-gradient(135deg, #7338a1 0%, #9d4edd 100%)',
                  border: 'none',
                  boxShadow: campaignCodes[campaign.id]
                    ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                    : '0 4px 12px rgba(115, 56, 161, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = campaignCodes[campaign.id]
                    ? '0 6px 16px rgba(16, 185, 129, 0.4)'
                    : '0 6px 16px rgba(115, 56, 161, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = campaignCodes[campaign.id]
                    ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                    : '0 4px 12px rgba(115, 56, 161, 0.3)';
                }}
              >
                {campaignCodes[campaign.id] ? 'Kodu Görüntüle' : 'Kod Oluştur'}
              </Button>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Referans Programı Açıklama Modalı */}
      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            <GiftOutlined style={{ color: '#7338a1', marginRight: '8px', fontSize: '20px' }} />
            <span style={{ color: '#7338a1', fontWeight: 'bold' }}>Referans Programı Nedir?</span>
          </div>
        }
        open={infoModalVisible}
        onCancel={() => setInfoModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setInfoModalVisible(false)} style={{ backgroundColor: '#7338a1', borderColor: '#7338a1' }}>
            Anladım
          </Button>
        ]}
        width={600}
        centered
      >
        <div style={{ padding: '16px 0' }}>
          <Title level={4} style={{ color: '#7338a1', marginBottom: '16px' }}>
            <PlayCircleOutlined style={{ marginRight: '8px' }} />
            Nasıl Çalışır?
          </Title>
          <Paragraph style={{ marginBottom: '16px', fontSize: '15px' }}>
            <strong>1. Kod Oluşturun:</strong> Aktif kampanyalardan birini seçerek kişisel referans kodunuzu oluşturun. 
          </Paragraph>
          <Paragraph style={{ marginBottom: '16px', fontSize: '15px' }}>
            <strong>2. Paylaşın:</strong> Kodunuzu diğer şirket yöneticileriyle paylaşın. Dilediğiniz kadar şirket yöneticisi ile paylaşabilirsiniz.
          </Paragraph>
          <Paragraph style={{ marginBottom: '16px', fontSize: '15px' }}>
            <strong>3. Ödül Kazanın:</strong> Kodunuzu kullanan şirketler sisteme kaydolup ödeme yaptığında siz de ödül kazanırsınız.
          </Paragraph>

          <Title level={4} style={{ color: '#7338a1', marginBottom: '16px', marginTop: '24px' }}>
            <DollarCircleOutlined style={{ marginRight: '8px' }} />
            Ödül Türleri
          </Title>
          <Paragraph style={{ marginBottom: '8px', fontSize: '15px' }}>
            • <strong>İndirim Ödülü:</strong> Ek kullanıcı satın alırken indirim
          </Paragraph>
        
          <Paragraph style={{ marginBottom: '16px', fontSize: '15px' }}>
            • <strong>Ücretsiz Gün:</strong> Aboneliğinize ek günler
          </Paragraph>

          <Alert
            message="İpucu"
            description="Kazandığınız indirim ödüllerini 'Şirket Ayarları > Plan Yönetimi' bölümünden ek kullanıcı satın alırken kullanabilirsiniz. Ücretsiz gün ödülü ise direkt abonelik bitiş tarihine eklenir."
            type="info"
            showIcon
            style={{ marginTop: '20px' }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default CreateReferralCode;

